import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import useAvailableStudyReports from "../../hooks/useAvailableStudyReports";
import { useRegisterEcrfFilledPdfReportMutation } from "../../hooks/useRegisterEcrfFilledPdfReportMutation";
import { useRegisterPoemFilledPdfReportMutation } from "../../hooks/useRegisterPoemFilledPdfReportMutation";
import {
  useReportStatusQuery,
  useStartDownloadZipMutation,
} from "../../hooks/useStartReportGenerationMutation";
import {
  useCancelStudyExportMutation,
  useReportEcrfFilledPdfOutcomeMutation,
  useReportPoemFilledPdfOutcomeMutation,
  useStartStudyExportMutation,
  useStudyExportStatusQuery,
} from "../../hooks/useStudyExportRequest";
import { SUBJECT_STATUSES, TYPES_OF_REPORTS } from "../../utils/constants";
import { formatFileSize } from "../../utils/helpers";
import {
  generateTrialArchiveEcrfZip,
  uploadEcrfFilledPdfZip,
} from "../../utils/archiveEcrfZipUtils";
import { generateTrialPoemZip, uploadPoemFilledPdfZip } from "../../utils/archivePoemZipUtils";
import CustomButton from "../UI/CustomButton";

// Mirrors MAX_CONCURRENT_STUDY_EXPORTS in reports/study_export.py — only this many exports may
// run across the whole platform at once; the rest queue and wait for a slot.
const MAX_CONCURRENT_STUDY_EXPORTS = 2;

// A study export excludes Pre-Screening/Screen-Failed subjects by default; the "Include
// Pre-Screening and Screen Failed Subjects" checkbox below opts them back in. Every other status
// is always included either way. Mirrors DEFAULT_STUDY_EXPORT_PATIENT_STATUSES/
// ALL_STUDY_EXPORT_PATIENT_STATUSES in reports/study_export.py.
const DEFAULT_STUDY_EXPORT_STATUSES = [
  SUBJECT_STATUSES.CONTINUING,
  SUBJECT_STATUSES.WITHDREW,
  SUBJECT_STATUSES.TERMINATED,
  SUBJECT_STATUSES.COMPLETED,
];
const ALL_STUDY_EXPORT_STATUSES = Object.values(SUBJECT_STATUSES);

// Report kinds whose "start" job only produces a manifest — a second start_*_zip
// job has to run before there's a downloadable file_url.
const ZIP_PHASE_REPORT_TYPES = new Set([
  TYPES_OF_REPORTS.PHOTO,
  TYPES_OF_REPORTS.EXTERNAL_PHOTOS,
  TYPES_OF_REPORTS.EXTERNAL_VIDEOS,
  TYPES_OF_REPORTS.VIDEO,
  TYPES_OF_REPORTS.EXTERNAL_DOCUMENTS,
  TYPES_OF_REPORTS.ECONSENT,
]);

const StatusIcon = ({ phase }) => {
  if (phase === "done") return <CheckCircleIcon fontSize="small" color="success" />;
  if (phase === "empty") return <RemoveCircleOutlineIcon fontSize="small" color="disabled" />;
  if (phase === "failed" || phase === "cancelled")
    return <CancelIcon fontSize="small" color="error" />;
  return <CircularProgress size={16} />;
};

// The report-kind job has already been dispatched server-side by the time this renders — this
// just watches it through to completion (and, for the zip-phase kinds, chains the second job).
const ReportProgressRow = ({
  reportType,
  label,
  jobId: initialJobId,
  statusUrl: initialStatusUrl,
  exportRequestId,
}) => {
  const [statusUrl, setStatusUrl] = useState(initialStatusUrl);
  const [phase, setPhase] = useState("generating"); // generating | zipping | done | empty | failed
  const [fileSizeBytes, setFileSizeBytes] = useState(null);

  const startZipMutation = useStartDownloadZipMutation();
  const { data: statusData } = useReportStatusQuery(statusUrl, { enabled: Boolean(statusUrl) });

  useEffect(() => {
    if (!statusData) return;

    if (statusData.status === "FAILED") {
      setPhase("failed");
      return;
    }
    if (statusData.status !== "SUCCESS") return;

    // A SUCCESS status with no file_url/manifest means the report ran fine but
    // there was no data for this trial (e.g. a POEM report with zero responses) —
    // that's not a failure, so it gets its own "empty" phase.
    if (phase === "generating") {
      if (statusData.file_url) {
        setPhase("done");
        setFileSizeBytes(statusData.file_size_bytes);
      } else if (statusData.manifest_blob_path && ZIP_PHASE_REPORT_TYPES.has(reportType)) {
        setPhase("zipping");
        startZipMutation.mutate(
          { reportType, jobId: statusData.job_id, exportRequestId },
          {
            onSuccess: ({ statusUrl: zipStatusUrl }) => setStatusUrl(zipStatusUrl),
            onError: () => setPhase("failed"),
          }
        );
      } else {
        setPhase("empty");
      }
    } else if (phase === "zipping") {
      setPhase(statusData.file_url ? "done" : "empty");
      setFileSizeBytes(statusData.file_url ? statusData.file_size_bytes : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusData]);

  const formattedSize = formatFileSize(fileSizeBytes);
  const text =
    phase === "done"
      ? `${label} ready${formattedSize ? ` (${formattedSize})` : ""}`
      : phase === "empty"
        ? `${label}: no data to export`
        : phase === "failed"
          ? `${label} failed`
          : `Generating ${label}…`;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      data-testid="export-study-progress-row"
      data-report-kind={reportType}
      data-phase={phase}
    >
      <StatusIcon phase={phase} />
      <Typography variant="body2" color={phase === "empty" ? "text.secondary" : "text.primary"}>
        {text}
      </Typography>
    </Stack>
  );
};

// eCRF Filled PDF has no backend job while it's running — it's generated entirely client-side
// (per-visit PDF rendering + JSZip), then uploaded so it can still join the combined bundle like
// every other report. Its outcome (including failure/no-data) is reported to the backend so the
// export request's completeness check can see it, matching every other report kind.
//
// `active` reflects whether the parent export request is currently RUNNING. It gates whether
// this row is even allowed to start (so resuming into an already-cancelled export doesn't kick
// off a fresh generation run), and an AbortController tied to it stops the per-visit
// get_ecrf_blocks_per_visit loop the moment the export is cancelled mid-flight — without it,
// nothing would tell this client-side loop to stop pinging that endpoint after Stop Export.
const EcrfFilledPdfProgressRow = ({
  trialId,
  exportRequestId,
  reportType,
  label,
  active,
  patientStatus,
}) => {
  const [phase, setPhase] = useState(active ? "generating" : "cancelled");
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [fileSizeBytes, setFileSizeBytes] = useState(null);
  const startedRef = useRef(false);

  const registerMutation = useRegisterEcrfFilledPdfReportMutation();
  const reportOutcomeMutation = useReportEcrfFilledPdfOutcomeMutation();

  useEffect(() => {
    if (startedRef.current || !active) return undefined;
    startedRef.current = true;

    const controller = new AbortController();

    const run = async () => {
      const token = localStorage.getItem("token");
      let latestProgress = { current: 0, total: 0 };

      let zipBlob;
      let failedVisits;
      let aborted;
      try {
        ({ zipBlob, failedVisits, aborted } = await generateTrialArchiveEcrfZip({
          trialId,
          token,
          signal: controller.signal,
          patientStatus,
          onProgress: (p) => {
            latestProgress = p;
            setProgress(p);
          },
        }));
      } catch (err) {
        if (controller.signal.aborted) {
          setPhase("cancelled");
          return;
        }
        setPhase("failed");
        reportOutcomeMutation.mutate({ exportRequestId, reason: "failed", error: err?.message });
        return;
      }

      if (aborted) {
        setPhase("cancelled");
        return;
      }

      if (!zipBlob) {
        setPhase("empty");
        reportOutcomeMutation.mutate({ exportRequestId, reason: "empty" });
        return;
      }

      setPhase("uploading");
      try {
        const blobPath = await uploadEcrfFilledPdfZip({
          zipBlob,
          token,
          signal: controller.signal,
        });
        await registerMutation.mutateAsync({
          blobPath,
          totalRows: latestProgress.total - failedVisits.length,
          exportRequestId,
          fileSizeBytes: zipBlob.size,
          signal: controller.signal,
        });
        setFileSizeBytes(zipBlob.size);
        setPhase("done");
      } catch (err) {
        if (controller.signal.aborted) {
          setPhase("cancelled");
          return;
        }
        setPhase("failed");
        reportOutcomeMutation.mutate({
          exportRequestId,
          reason: "failed",
          error: err?.message,
        });
      }
    };

    run();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const formattedSize = formatFileSize(fileSizeBytes);
  const text =
    phase === "done"
      ? `${label} ready${formattedSize ? ` (${formattedSize})` : ""}`
      : phase === "empty"
        ? `${label}: no data to export`
        : phase === "cancelled"
          ? `${label} cancelled`
          : phase === "failed"
            ? `${label} failed`
            : phase === "uploading"
              ? `Uploading ${label}…`
              : `Generating ${label} (${progress.current}/${progress.total})…`;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      data-testid="export-study-progress-row"
      data-report-kind={reportType}
      data-phase={phase}
    >
      <StatusIcon phase={phase} />
      <Typography variant="body2" color={phase === "empty" ? "text.secondary" : "text.primary"}>
        {text}
      </Typography>
    </Stack>
  );
};

// POEM Filled PDF has no backend job while it's running — same client-side-generated pattern as
// EcrfFilledPdfProgressRow above, just backed by generateTrialPoemZip/uploadPoemFilledPdfZip and
// the report-kind-parametrized register/outcome endpoints (one component covers both the combined
// and individual-visits kinds, distinguished by `individualVisits`).
const PoemFilledPdfProgressRow = ({
  trialId,
  exportRequestId,
  reportType,
  label,
  active,
  individualVisits,
  patientStatus,
}) => {
  const [phase, setPhase] = useState(active ? "generating" : "cancelled");
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [fileSizeBytes, setFileSizeBytes] = useState(null);
  const startedRef = useRef(false);

  const registerMutation = useRegisterPoemFilledPdfReportMutation();
  const reportOutcomeMutation = useReportPoemFilledPdfOutcomeMutation();

  useEffect(() => {
    if (startedRef.current || !active) return undefined;
    startedRef.current = true;

    const controller = new AbortController();

    const run = async () => {
      const token = localStorage.getItem("token");
      let latestProgress = { current: 0, total: 0 };

      let zipBlob;
      let failedEntries;
      let aborted;
      try {
        ({ zipBlob, failedEntries, aborted } = await generateTrialPoemZip({
          manifestPayload: {
            trials: [{ id: trialId }],
            ...(patientStatus ? { patient_status: patientStatus } : {}),
          },
          token,
          individualVisits,
          signal: controller.signal,
          onProgress: (p) => {
            latestProgress = p;
            setProgress(p);
          },
        }));
      } catch (err) {
        if (controller.signal.aborted) {
          setPhase("cancelled");
          return;
        }
        setPhase("failed");
        reportOutcomeMutation.mutate({
          exportRequestId,
          reportKind: reportType,
          reason: "failed",
          error: err?.message,
        });
        return;
      }

      if (aborted) {
        setPhase("cancelled");
        return;
      }

      if (!zipBlob) {
        setPhase("empty");
        reportOutcomeMutation.mutate({ exportRequestId, reportKind: reportType, reason: "empty" });
        return;
      }

      setPhase("uploading");
      try {
        const blobPath = await uploadPoemFilledPdfZip({
          zipBlob,
          token,
          signal: controller.signal,
        });
        await registerMutation.mutateAsync({
          reportKind: reportType,
          blobPath,
          totalRows: latestProgress.total - failedEntries.length,
          exportRequestId,
          fileSizeBytes: zipBlob.size,
          signal: controller.signal,
        });
        setFileSizeBytes(zipBlob.size);
        setPhase("done");
      } catch (err) {
        if (controller.signal.aborted) {
          setPhase("cancelled");
          return;
        }
        setPhase("failed");
        reportOutcomeMutation.mutate({
          exportRequestId,
          reportKind: reportType,
          reason: "failed",
          error: err?.message,
        });
      }
    };

    run();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const formattedSize = formatFileSize(fileSizeBytes);
  const text =
    phase === "done"
      ? `${label} ready${formattedSize ? ` (${formattedSize})` : ""}`
      : phase === "empty"
        ? `${label}: no data to export`
        : phase === "cancelled"
          ? `${label} cancelled`
          : phase === "failed"
            ? `${label} failed`
            : phase === "uploading"
              ? `Uploading ${label}…`
              : `Generating ${label} (${progress.current}/${progress.total})…`;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      data-testid="export-study-progress-row"
      data-report-kind={reportType}
      data-phase={phase}
    >
      <StatusIcon phase={phase} />
      <Typography variant="body2" color={phase === "empty" ? "text.secondary" : "text.primary"}>
        {text}
      </Typography>
    </Stack>
  );
};

const ExportStudyReportModal = ({ open, onClose, trialId }) => {
  const [step, setStep] = useState("select"); // select | queued | progress
  const [selectedKinds, setSelectedKinds] = useState([]);
  const [exportRequestId, setExportRequestId] = useState(null);
  const [includePreScreeningAndScreenFailed, setIncludePreScreeningAndScreenFailed] =
    useState(false);

  const { data: availableData, isLoading: isLoadingAvailableReports } = useAvailableStudyReports({
    trialId,
    enabled: open,
  });
  const availableReports = availableData?.reports;
  const activeRequest = availableData?.activeRequest;

  const queryClient = useQueryClient();
  const startExportMutation = useStartStudyExportMutation();
  const cancelExportMutation = useCancelStudyExportMutation();
  const { data: wrapperStatus } = useStudyExportStatusQuery(exportRequestId, {
    enabled: open && Boolean(exportRequestId),
  });

  const allSelected =
    availableReports?.length > 0 && selectedKinds.length === availableReports.length;
  const someSelected = selectedKinds.length > 0 && !allSelected;

  const jobsByKind = useMemo(() => {
    const map = {};
    (wrapperStatus?.report_jobs || []).forEach((job) => {
      map[job.report_kind] = job;
    });
    return map;
  }, [wrapperStatus]);

  const runningKinds = wrapperStatus?.selected_report_kinds || [];
  const allKindsTerminal =
    runningKinds.length > 0 &&
    runningKinds.every(
      (kind) => jobsByKind[kind] && ["SUCCESS", "FAILED"].includes(jobsByKind[kind].status)
    );
  const hadRealFailure = runningKinds.some((kind) => jobsByKind[kind]?.status === "FAILED");

  // "cancelled" mirrors StudyExportRequest.FAIL_CANCELLED in reports/models.py -- unlike the
  // other phases below it can be known the moment the user stops the export, before every
  // individual report kind has necessarily reached its own terminal state.
  const isCancelled = wrapperStatus?.fail_reason === "cancelled";
  const showSummary = allKindsTerminal || isCancelled;

  // idle | no_data | running | done | failed | cancelled — matches the vocabulary this
  // data-phase attribute has always exposed, even though the wrapper status underneath is now
  // QUEUED/RUNNING/SUCCESS/FAILED.
  const bundlePhase = isCancelled
    ? "cancelled"
    : !allKindsTerminal
      ? "idle"
      : wrapperStatus.status === "SUCCESS"
        ? wrapperStatus.file_url
          ? "done"
          : "no_data"
        : wrapperStatus.status === "FAILED"
          ? "failed"
          : "running";

  // Leave "queued" the moment the underlying request stops being QUEUED — whether it was
  // promoted to RUNNING, or ended (cancelled/reaped) before ever getting a slot.
  useEffect(() => {
    if (step === "queued" && wrapperStatus?.status && wrapperStatus.status !== "QUEUED") {
      setStep("progress");
    }
  }, [step, wrapperStatus?.status]);

  // A user can only ever have one active export per trial (see try_admit_or_queue on the
  // backend) -- opening the modal on a trial that already has one just reconnects to it,
  // automatically, rather than making the user notice a banner and click through to it.
  useEffect(() => {
    if (step === "select" && !exportRequestId && activeRequest) {
      setExportRequestId(activeRequest.id);
      setStep(activeRequest.status === "QUEUED" ? "queued" : "progress");
    }
  }, [step, exportRequestId, activeRequest]);

  const isInProgress =
    Boolean(exportRequestId) &&
    (!wrapperStatus || !["SUCCESS", "FAILED"].includes(wrapperStatus.status));

  useEffect(() => {
    if (!isInProgress) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isInProgress]);

  const toggleKind = (kind) => {
    setSelectedKinds((prev) =>
      prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind]
    );
  };

  const toggleSelectAll = () => {
    setSelectedKinds(allSelected ? [] : (availableReports || []).map((r) => r.kind));
  };

  // Once connected to an actual request (fresh or reconnected-to-active), the checkbox's actual
  // effect was decided server-side at dispatch time (reports/study_export.py) and is echoed back
  // via wrapperStatus -- that's the source of truth for the client-driven ECRF/POEM Filled PDF
  // rows below, not local state, which resets to false on reconnect (see the activeRequest effect
  // above) regardless of what was actually chosen when the export was started.
  const includePreScreeningAndScreenFailedEffective = exportRequestId
    ? Boolean(wrapperStatus?.include_pre_screening_and_screen_failed)
    : includePreScreeningAndScreenFailed;
  const patientStatusPayload = (
    includePreScreeningAndScreenFailedEffective
      ? ALL_STUDY_EXPORT_STATUSES
      : DEFAULT_STUDY_EXPORT_STATUSES
  ).map((value) => ({ value }));

  const handleDownloadClick = () => {
    startExportMutation.mutate(
      { trialId, reportKinds: selectedKinds, includePreScreeningAndScreenFailed },
      {
        onSuccess: ({ exportRequestId: newExportRequestId, status: newStatus }) => {
          setExportRequestId(newExportRequestId);
          setStep(newStatus === "RUNNING" ? "progress" : "queued");
        },
      }
    );
  };

  const handleCancelExport = () => {
    cancelExportMutation.mutate(
      { exportRequestId },
      {
        onSuccess: () =>
          queryClient.invalidateQueries({ queryKey: ["study-export-status", exportRequestId] }),
      }
    );
  };

  const handleClose = () => {
    if (isInProgress) return;
    setStep("select");
    setSelectedKinds([]);
    setExportRequestId(null);
    setIncludePreScreeningAndScreenFailed(false);
    onClose();
  };

  const selectedLabelByKind = useMemo(() => {
    const map = {};
    (availableReports || []).forEach((r) => {
      map[r.kind] = r.label;
    });
    return map;
  }, [availableReports]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isInProgress}
    >
      <DialogTitle>Export Study Report</DialogTitle>
      <DialogContent>
        {step === "select" && (
          <Stack spacing={1} sx={{ mt: 1 }}>
            {/* If there's already an active export for this trial, the effect above redirects
                straight into "queued"/"progress" — this step only ever renders with activeRequest
                unset, or for the brief instant before that redirect happens. */}
            {isLoadingAvailableReports && <CircularProgress size={20} />}

            {!isLoadingAvailableReports && availableReports?.length === 0 && (
              <Typography variant="body2">No reports are available for this trial.</Typography>
            )}

            {!isLoadingAvailableReports && availableReports?.length > 0 && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includePreScreeningAndScreenFailed}
                      onChange={(e) => setIncludePreScreeningAndScreenFailed(e.target.checked)}
                      inputProps={{
                        "data-testid": "export-study-include-pre-screening-checkbox-input",
                      }}
                    />
                  }
                  label="Include Pre-Screening and Screen Failed Subjects"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={toggleSelectAll}
                      inputProps={{ "data-testid": "export-study-select-all-checkbox-input" }}
                    />
                  }
                  label={`Select All (${availableReports.length} items)`}
                />
                {availableReports.map((report) => (
                  <FormControlLabel
                    key={report.kind}
                    control={
                      <Checkbox
                        checked={selectedKinds.includes(report.kind)}
                        onChange={() => toggleKind(report.kind)}
                        inputProps={{
                          "data-testid": "export-study-report-checkbox-input",
                          "data-report-kind": report.kind,
                        }}
                      />
                    }
                    label={report.label}
                  />
                ))}
              </>
            )}
          </Stack>
        )}

        {step === "queued" && (
          <Stack spacing={2} sx={{ mt: 1 }} data-testid="export-study-queued-step">
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={16} />
              <Typography variant="body2">
                {wrapperStatus?.queue_position
                  ? `You're #${wrapperStatus.queue_position} in line — only ${MAX_CONCURRENT_STUDY_EXPORTS} exports can run at a time. This will start automatically once a slot opens up.`
                  : "Waiting for a slot to open up…"}
              </Typography>
            </Stack>
          </Stack>
        )}

        {step === "progress" && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography color="error" variant="body2">
              Do not close or refresh this browser tab/window until the download is ready.
            </Typography>

            {/* Only meaningful once the request actually got a slot and dispatched its jobs —
                if it was cancelled/reaped while still queued, there's nothing to show progress
                on and the summary section below explains what happened instead. */}
            {wrapperStatus?.started_at && (
              <Stack spacing={1}>
                {runningKinds.map((kind) =>
                  kind === TYPES_OF_REPORTS.ECRF_FILLED_PDF ? (
                    <EcrfFilledPdfProgressRow
                      key={kind}
                      trialId={trialId}
                      exportRequestId={exportRequestId}
                      reportType={kind}
                      label={selectedLabelByKind[kind] || kind}
                      active={wrapperStatus?.status === "RUNNING"}
                      patientStatus={patientStatusPayload}
                    />
                  ) : kind === TYPES_OF_REPORTS.POEM_FILLED_PDF ||
                    kind === TYPES_OF_REPORTS.POEM_FILLED_PDF_INDIVIDUAL ? (
                    <PoemFilledPdfProgressRow
                      key={kind}
                      trialId={trialId}
                      exportRequestId={exportRequestId}
                      reportType={kind}
                      label={selectedLabelByKind[kind] || kind}
                      active={wrapperStatus?.status === "RUNNING"}
                      individualVisits={kind === TYPES_OF_REPORTS.POEM_FILLED_PDF_INDIVIDUAL}
                      patientStatus={patientStatusPayload}
                    />
                  ) : (
                    <ReportProgressRow
                      key={kind}
                      reportType={kind}
                      label={selectedLabelByKind[kind] || kind}
                      jobId={jobsByKind[kind]?.job_id}
                      statusUrl={jobsByKind[kind]?.status_url}
                      exportRequestId={exportRequestId}
                    />
                  )
                )}
              </Stack>
            )}

            {showSummary && (
              <>
                {/* bundlePhase keeps the pre-queueing vocabulary (idle/no_data/running/done/failed)
                    that the export-study-bundle-status data-phase attribute has always exposed,
                    plus "cancelled" for a user-initiated stop. */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  data-testid="export-study-bundle-status"
                  data-phase={bundlePhase}
                >
                  {bundlePhase !== "no_data" && <StatusIcon phase={bundlePhase} />}
                  <Typography variant="body2">
                    {bundlePhase === "cancelled"
                      ? "Export cancelled."
                      : bundlePhase === "no_data"
                        ? hadRealFailure
                          ? "None of the selected reports could be generated."
                          : "None of the selected reports had data to export for this trial."
                        : bundlePhase === "failed"
                          ? "Preparing combined download failed"
                          : bundlePhase === "done"
                            ? `Combined download ready.${
                                formatFileSize(wrapperStatus.file_size_bytes)
                                  ? ` (${formatFileSize(wrapperStatus.file_size_bytes)})`
                                  : ""
                              }`
                            : "Preparing combined download…"}
                  </Typography>
                </Stack>

                {bundlePhase === "done" && (
                  <CustomButton
                    onClick={() => window.open(wrapperStatus.file_url, "_blank")}
                    sx={{ alignSelf: "center", minWidth: 160 }}
                    data-testid="export-study-download-zip-btn"
                  >
                    Download ZIP
                  </CustomButton>
                )}

                {(bundlePhase === "failed" || bundlePhase === "cancelled") &&
                  runningKinds.some(
                    (kind) => jobsByKind[kind]?.status === "SUCCESS" && jobsByKind[kind]?.file_url
                  ) && (
                    <Stack spacing={1}>
                      <Typography variant="body2">
                        {bundlePhase === "cancelled"
                          ? "Here are the reports that finished before you stopped the export:"
                          : "Some reports finished but the combined download could not be prepared. You can still " +
                            "download the reports that succeeded individually:"}
                      </Typography>
                      {runningKinds
                        .filter(
                          (kind) =>
                            jobsByKind[kind]?.status === "SUCCESS" && jobsByKind[kind]?.file_url
                        )
                        .map((kind) => {
                          const formattedSize = formatFileSize(jobsByKind[kind]?.file_size_bytes);
                          return (
                            <Button
                              key={kind}
                              size="small"
                              onClick={() => window.open(jobsByKind[kind].file_url, "_blank")}
                            >
                              Download {selectedLabelByKind[kind] || kind}
                              {formattedSize ? ` (${formattedSize})` : ""}
                            </Button>
                          );
                        })}
                    </Stack>
                  )}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {step === "select" && (
          <>
            <Button onClick={handleClose} data-testid="export-study-cancel-btn">
              Cancel
            </Button>
            <CustomButton
              onClick={handleDownloadClick}
              disabled={selectedKinds.length === 0}
              data-testid="export-study-download-btn"
            >
              Download
            </CustomButton>
          </>
        )}
        {(step === "queued" || step === "progress") && isInProgress && (
          <Button
            onClick={handleCancelExport}
            disabled={cancelExportMutation.isPending}
            color="error"
            data-testid="export-study-stop-export-btn"
          >
            Stop Export
          </Button>
        )}
        {(step === "queued" || step === "progress") && !isInProgress && (
          <Button onClick={handleClose} data-testid="export-study-close-btn">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ExportStudyReportModal;
